<template>
  <div class="training-dashboard-view pa-4">
    <!-- Header Neutro -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="d-flex align-center py-4 px-5 flex-wrap justify-space-between">
        <div class="d-flex align-center">
          <v-icon color="primary" large class="mr-3">mdi-view-dashboard-outline</v-icon>
          <div>
            <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
              Dashboard de Cobertura de Capacitaciones
            </h2>
            <span class="text-caption grey--text text--darken-2">
              Indicadores clave de cumplimiento SST, avance de personal capacitado por área y puesto vs. dotación activa.
            </span>
          </div>
        </div>

        <div class="d-flex align-center mt-2 mt-sm-0">
          <v-btn
            color="success"
            small
            outlined
            class="rounded-lg text-none font-weight-bold mr-2"
            @click="exportToExcel"
            :disabled="loading"
          >
            <v-icon left small>mdi-file-excel</v-icon>
            Exportar Cobertura
          </v-btn>
          <v-btn
            color="primary"
            small
            class="rounded-lg text-none font-weight-bold"
            elevation="0"
            :loading="loading"
            @click="fetchCoverage"
          >
            <v-icon left small>mdi-refresh</v-icon>
            Actualizar
          </v-btn>
        </div>
      </v-card-title>
    </v-card>

    <!-- KPI Cards Strip -->
    <v-row dense class="mb-4">
      <!-- 1. Dotación Total Activa -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-xl pa-4 bg-white border-light h-100" outlined elevation="0">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                Personal Activo
              </div>
              <div class="text-h4 font-weight-black grey--text text--darken-4 mt-1">
                {{ summary.TotalWorkforce || 0 }}
              </div>
              <div class="text-caption grey--text text--darken-1 mt-1">
                Contratos vigentes en planta/campo
              </div>
            </div>
            <v-avatar color="grey lighten-4" size="48">
              <v-icon color="grey darken-3">mdi-account-group-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <!-- 2. Personal Capacitado -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-xl pa-4 bg-white border-light h-100" outlined elevation="0">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                Personal Capacitado
              </div>
              <div class="text-h4 font-weight-black green--text text--darken-3 mt-1">
                {{ summary.TotalTrained || 0 }}
              </div>
              <div class="text-caption green--text text--darken-2 mt-1">
                Con asistencia validada
              </div>
            </div>
            <v-avatar color="green lighten-5" size="48">
              <v-icon color="green darken-2">mdi-account-check-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <!-- 3. Personal Pendiente -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-xl pa-4 bg-white border-light h-100" outlined elevation="0">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                Personal Pendiente
              </div>
              <div class="text-h4 font-weight-black amber--text text--darken-4 mt-1">
                {{ summary.TotalPending || 0 }}
              </div>
              <div class="text-caption amber--text text--darken-3 mt-1">
                Brecha por capacitar
              </div>
            </div>
            <v-avatar color="amber lighten-5" size="48">
              <v-icon color="amber darken-3">mdi-account-clock-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <!-- 4. Porcentaje de Cobertura Global -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-xl pa-4 bg-white border-light h-100" outlined elevation="0">
          <div>
            <div class="d-flex align-center justify-space-between">
              <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                Cobertura Global
              </div>
              <v-avatar :color="getCoverageBgColor(summary.CoveragePercentage)" size="36">
                <v-icon :color="getCoverageColor(summary.CoveragePercentage)" small>
                  mdi-chart-donut
                </v-icon>
              </v-avatar>
            </div>
            <div class="d-flex align-baseline mt-1">
              <span class="text-h4 font-weight-black" :class="getCoverageTextColor(summary.CoveragePercentage)">
                {{ summary.CoveragePercentage || 0 }}%
              </span>
              <span class="text-caption grey--text ml-2">de meta alcanzada</span>
            </div>
            <v-progress-linear
              :value="summary.CoveragePercentage || 0"
              :color="getCoverageColor(summary.CoveragePercentage)"
              height="8"
              rounded
              class="mt-2"
            ></v-progress-linear>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filtros de Búsqueda -->
    <v-card class="mb-5 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="py-3 px-5 d-flex align-center">
        <v-icon color="grey darken-3" small class="mr-2">mdi-filter-variant</v-icon>
        <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
          Filtros de Segmentación
        </span>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="py-4 px-5">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="3">
            <s-select
              label="Evento de Capacitación"
              :items="eventItems"
              v-model="filters.TevID"
              clearable
              @change="fetchCoverage"
            />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <s-select
              label="Tema / Curso"
              :items="topicItems"
              v-model="filters.TtpID"
              clearable
              @change="fetchCoverage"
            />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <s-select-area label="Área de la Empresa" v-model="filters.AreID" clearable @change="fetchCoverage" />
          </v-col>

          <v-col cols="12" sm="3" md="1.5">
            <s-date label="Fecha Desde" v-model="filters.DateInitial" clearable />
          </v-col>

          <v-col cols="12" sm="3" md="1.5">
            <s-date label="Fecha Hasta" v-model="filters.DateFin" clearable />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Paneles de Tablas: Cobertura por Área vs Cobertura por Puesto -->
    <v-card class="rounded-xl border-light bg-white mb-5" outlined elevation="0">
      <v-tabs v-model="activeTab" color="primary" class="px-3 pt-2">
        <v-tab class="text-none font-weight-bold">
          <v-icon left small>mdi-domain</v-icon>
          Cobertura por Área ({{ byArea.length }})
        </v-tab>
        <v-tab class="text-none font-weight-bold">
          <v-icon left small>mdi-briefcase-outline</v-icon>
          Cobertura por Puesto de Trabajo ({{ byPosition.length }})
        </v-tab>
      </v-tabs>
      <v-divider></v-divider>

      <v-tabs-items v-model="activeTab" class="pa-4">
        <!-- TAB 1: COBERTURA POR ÁREA -->
        <v-tab-item>
          <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
            <div class="text-caption grey--text text--darken-2">
              Cumplimiento comparativo de personal capacitado frente al total de colaboradores asignados a cada área.
            </div>
            <v-text-field
              v-model="searchArea"
              append-icon="mdi-magnify"
              label="Filtrar área..."
              single-line
              hide-details
              dense
              outlined
              class="rounded-lg max-w-250 mt-2 mt-sm-0"
            ></v-text-field>
          </div>

          <v-data-table
            :headers="areaHeaders"
            :items="byArea"
            :search="searchArea"
            :loading="loading"
            dense
            class="border-rounded elevation-0"
            :items-per-page="15"
          >
            <template v-slot:[`item.AreName`]="{ item }">
              <span class="font-weight-bold grey--text text--darken-4">{{ item.AreName }}</span>
            </template>

            <template v-slot:[`item.TotalWorkforce`]="{ item }">
              <span class="font-weight-medium">{{ item.TotalWorkforce }}</span>
            </template>

            <template v-slot:[`item.TotalTrained`]="{ item }">
              <span class="font-weight-bold green--text text--darken-3">{{ item.TotalTrained }}</span>
            </template>

            <template v-slot:[`item.TotalPending`]="{ item }">
              <span class="font-weight-bold amber--text text--darken-3">{{ item.TotalPending }}</span>
            </template>

            <template v-slot:[`item.CoveragePercentage`]="{ item }">
              <div class="d-flex align-center" style="min-width: 140px;">
                <v-progress-linear
                  :value="item.CoveragePercentage"
                  :color="getCoverageColor(item.CoveragePercentage)"
                  height="7"
                  rounded
                  class="mr-2"
                ></v-progress-linear>
                <span class="text-caption font-weight-bold" :class="getCoverageTextColor(item.CoveragePercentage)">
                  {{ item.CoveragePercentage }}%
                </span>
              </div>
            </template>

            <template v-slot:[`item.status`]="{ item }">
              <v-chip x-small :color="getCoverageChipColor(item.CoveragePercentage)" dark class="font-weight-bold">
                {{ getCoverageStatusText(item.CoveragePercentage) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- TAB 2: COBERTURA POR PUESTO -->
        <v-tab-item>
          <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
            <div class="text-caption grey--text text--darken-2">
              Desglose detallado por perfil de cargo / puesto de trabajo para identificación de puestos críticos pendientes.
            </div>
            <v-text-field
              v-model="searchPosition"
              append-icon="mdi-magnify"
              label="Filtrar puesto o área..."
              single-line
              hide-details
              dense
              outlined
              class="rounded-lg max-w-250 mt-2 mt-sm-0"
            ></v-text-field>
          </div>

          <v-data-table
            :headers="positionHeaders"
            :items="byPosition"
            :search="searchPosition"
            :loading="loading"
            dense
            class="border-rounded elevation-0"
            :items-per-page="15"
          >
            <template v-slot:[`item.PstName`]="{ item }">
              <span class="font-weight-bold grey--text text--darken-4">{{ item.PstName }}</span>
            </template>

            <template v-slot:[`item.AreName`]="{ item }">
              <span class="text-caption grey--text text--darken-2">{{ item.AreName }}</span>
            </template>

            <template v-slot:[`item.TotalWorkforce`]="{ item }">
              <span class="font-weight-medium">{{ item.TotalWorkforce }}</span>
            </template>

            <template v-slot:[`item.TotalTrained`]="{ item }">
              <span class="font-weight-bold green--text text--darken-3">{{ item.TotalTrained }}</span>
            </template>

            <template v-slot:[`item.TotalPending`]="{ item }">
              <span class="font-weight-bold amber--text text--darken-3">{{ item.TotalPending }}</span>
            </template>

            <template v-slot:[`item.CoveragePercentage`]="{ item }">
              <div class="d-flex align-center" style="min-width: 140px;">
                <v-progress-linear
                  :value="item.CoveragePercentage"
                  :color="getCoverageColor(item.CoveragePercentage)"
                  height="7"
                  rounded
                  class="mr-2"
                ></v-progress-linear>
                <span class="text-caption font-weight-bold" :class="getCoverageTextColor(item.CoveragePercentage)">
                  {{ item.CoveragePercentage }}%
                </span>
              </div>
            </template>

            <template v-slot:[`item.status`]="{ item }">
              <v-chip x-small :color="getCoverageChipColor(item.CoveragePercentage)" dark class="font-weight-bold">
                {{ getCoverageStatusText(item.CoveragePercentage) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import SSelect from "@/components/Utils/SSelect.vue";
import SSelectArea from "@/components/Utils/SSelectArea.vue";
import SDate from "@/components/Utils/SDate.vue";
import TrainingParticipantService from "@/services/Management/TrainingParticipantService";
import TrainingEventService from "@/services/Management/TrainingEventService";
import TrainingTopicService from "@/services/Management/TrainingTopicService";

export default {
  name: "TrainingDashboard",
  components: {
    SSelect,
    SSelectArea,
    SDate,
  },
  data() {
    return {
      loading: false,
      activeTab: 0,
      searchArea: "",
      searchPosition: "",
      eventItems: [],
      topicItems: [],
      filters: {
        TevID: null,
        TtpID: null,
        AreID: null,
        DateInitial: null,
        DateFin: null,
      },
      summary: {
        TotalWorkforce: 0,
        TotalTrained: 0,
        TotalPending: 0,
        CoveragePercentage: 0,
        TotalEvents: 0,
        TotalSessions: 0,
      },
      byArea: [],
      byPosition: [],
      areaHeaders: [
        { text: "Área de la Empresa", value: "AreName", align: "start" },
        { text: "Dotación Activa", value: "TotalWorkforce", align: "center", width: "130px" },
        { text: "Capacitados", value: "TotalTrained", align: "center", width: "130px" },
        { text: "Pendientes", value: "TotalPending", align: "center", width: "130px" },
        { text: "% Cobertura", value: "CoveragePercentage", align: "center", width: "180px" },
        { text: "Estado", value: "status", align: "center", width: "120px" },
      ],
      positionHeaders: [
        { text: "Puesto / Cargo", value: "PstName", align: "start" },
        { text: "Área Asignada", value: "AreName", width: "180px" },
        { text: "Dotación Activa", value: "TotalWorkforce", align: "center", width: "120px" },
        { text: "Capacitados", value: "TotalTrained", align: "center", width: "120px" },
        { text: "Pendientes", value: "TotalPending", align: "center", width: "120px" },
        { text: "% Cobertura", value: "CoveragePercentage", align: "center", width: "180px" },
        { text: "Estado", value: "status", align: "center", width: "120px" },
      ],
    };
  },
  created() {
    this.loadCatalogs();
    this.fetchCoverage();
  },
  methods: {
    loadCatalogs() {
      const reqId = this.$fun.getUserID();
      TrainingEventService.list({ SecStatus: 1 }, reqId).then((res) => {
        this.eventItems = (res.data || []).map((e) => ({ text: e.TevName, value: e.TevID }));
      });
      TrainingTopicService.list({ SecStatus: 1 }, reqId).then((res) => {
        this.topicItems = (res.data || []).map((t) => ({ text: t.TtpTitle, value: t.TtpID }));
      });
    },
    fetchCoverage() {
      this.loading = true;
      const reqId = this.$fun.getUserID();
      TrainingParticipantService.getCoverage(this.filters, reqId)
        .then((res) => {
          const data = res.data || {};
          this.summary = {
            TotalWorkforce: data.TotalHeadcount ?? data.Summary?.TotalWorkforce ?? 0,
            TotalTrained: data.TotalTrained ?? data.Summary?.TotalTrained ?? 0,
            TotalPending: data.TotalPending ?? data.Summary?.TotalPending ?? 0,
            CoveragePercentage: data.GlobalCoveragePercentage ?? data.Summary?.CoveragePercentage ?? 0,
            TotalEvents: data.EventCoverage?.length ?? data.Summary?.TotalEvents ?? 0,
            TotalSessions: data.Summary?.TotalSessions ?? 0,
          };
          const rawAreas = Array.isArray(data.AreaCoverage) ? data.AreaCoverage : (Array.isArray(data.ByArea) ? data.ByArea : []);
          this.byArea = rawAreas.map((a) => ({
            AreID: a.AreID || 0,
            AreName: a.AreName || "Sin Área Asignada",
            TotalWorkforce: a.TotalWorkers ?? a.TotalWorkforce ?? 0,
            TotalTrained: a.TrainedWorkers ?? a.TotalTrained ?? 0,
            TotalPending: a.PendingWorkers ?? a.TotalPending ?? 0,
            CoveragePercentage: a.CoveragePercentage ?? 0,
          }));
          const rawPositions = Array.isArray(data.PositionCoverage) ? data.PositionCoverage : (Array.isArray(data.ByPosition) ? data.ByPosition : []);
          this.byPosition = rawPositions.map((p) => ({
            PstID: p.PstID || 0,
            PstName: p.PstName || "Sin Puesto Definido",
            AreID: p.AreID || 0,
            AreName: p.AreName || "General",
            TotalWorkforce: p.TotalWorkers ?? p.TotalWorkforce ?? 0,
            TotalTrained: p.TrainedWorkers ?? p.TotalTrained ?? 0,
            TotalPending: p.PendingWorkers ?? p.TotalPending ?? 0,
            CoveragePercentage: p.CoveragePercentage ?? 0,
          }));
        })
        .catch(() => {
          this.$fun.alert("Error al cargar indicadores de cobertura.", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getCoverageColor(pct) {
      if (pct >= 80) return "green darken-2";
      if (pct >= 50) return "amber darken-2";
      return "red darken-1";
    },
    getCoverageBgColor(pct) {
      if (pct >= 80) return "green lighten-5";
      if (pct >= 50) return "amber lighten-5";
      return "red lighten-5";
    },
    getCoverageTextColor(pct) {
      if (pct >= 80) return "green--text text--darken-3";
      if (pct >= 50) return "amber--text text--darken-4";
      return "red--text text--darken-2";
    },
    getCoverageChipColor(pct) {
      if (pct >= 80) return "success";
      if (pct >= 50) return "warning";
      return "error";
    },
    getCoverageStatusText(pct) {
      if (pct >= 80) return "Óptimo";
      if (pct >= 50) return "En Proceso";
      return "Crítico";
    },
    exportToExcel() {
      // Exportación rápida de los datos tabulares
      const items = this.activeTab === 0 ? this.byArea : this.byPosition;
      if (!items || items.length === 0) {
        this.$fun.alert("No hay datos para exportar.", "info");
        return;
      }
      this.$fun.alert("Generando reporte de cobertura...", "info");
      // En Vue se puede descargar como CSV o XLSX usando los datos actuales
      let csvContent = "data:text/csv;charset=utf-8,";
      if (this.activeTab === 0) {
        csvContent += "Area,Dotacion_Activa,Capacitados,Pendientes,Porcentaje_Cobertura\n";
        items.forEach((r) => {
          csvContent += `"${r.AreName}",${r.TotalWorkforce},${r.TotalTrained},${r.TotalPending},${r.CoveragePercentage}%\n`;
        });
      } else {
        csvContent += "Puesto,Area,Dotacion_Activa,Capacitados,Pendientes,Porcentaje_Cobertura\n";
        items.forEach((r) => {
          csvContent += `"${r.PstName}","${r.AreName}",${r.TotalWorkforce},${r.TotalTrained},${r.TotalPending},${r.CoveragePercentage}%\n`;
        });
      }
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `Cobertura_Capacitacion_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.border-light {
  border: 1px solid #e2e8f0 !important;
}

.max-w-250 {
  max-width: 250px;
}
</style>
